import React, { useContext, useEffect, useState, useRef } from "react";
import { groupMoveInByCategory, leaseMoveInPhotos } from "../../utils/vacatePhotos";
import Ctx from "../../contexts/AppContext";
import { api } from "../../api";
import { crd, btnP, btnSec, sp, rad, type } from "../../constants/styles";
import Ic from "../common/Ic";
import { uploadPhoto } from "../../utils/supabaseClient";
import { formatPropAddr } from "../../utils/propertyAddr";
import SignaturePad from "./SignaturePad";
import { openSettlementPdf } from "../../utils/settlementPdf";

const CATEGORY_META = {
  hall:         { label: "Hall / Living Room", icon: "🛋️" },
  bedroom:      { label: "Bedroom",            icon: "🛏️" },
  kitchen:      { label: "Kitchen",            icon: "🍳" },
  bathroom:     { label: "Bathroom",           icon: "🚿" },
  walls:        { label: "Walls",              icon: "🧱" },
  meter:        { label: "Meter Reading",      icon: "📊" },
  other:        { label: "Other",              icon: "📷" },
  damage_proof: { label: "Damage Proof",       icon: "⚠️" },
};

// Lease photos → vacate-category mapper now lives in a shared util so
// both VacateDetailPage (landlord comparison) and VacatePage (tenant
// move-out upload) use the SAME synonym table. Avoids drift where
// one screen shows "Bed Room" matched but the other doesn't.

const DEDUCTION_REASONS = [
  { id: 'damage',       label: "Property Damage" },
  { id: 'cleaning',     label: "Cleaning" },
  { id: 'painting',     label: "Painting" },
  { id: 'unpaid_bills', label: "Unpaid Bills" },
  { id: 'other',        label: "Other" },
];

// Damage-proof uploader. Lifted to MODULE SCOPE so the component
// type is stable across VacateDetailPage re-renders. Previously it
// was nested inside the parent, which meant every parent state
// update created a NEW component function and React unmounted +
// remounted the entire subtree — including the hidden file <input>
// refs. Symptom: 'Take Proof' / 'Gallery' would sometimes open the
// picker but the onChange never fired (the user was tapping on a
// now-detached input). Same fix as the tenant-side VacatePage.
function ProofUploader({ selectedIds, onChange, vr, damageProofs, setPhotos, flash, h }) {
  const cameraRef = useRef(null);
  const galleryRef = useRef(null);
  const [busy, setBusy] = useState(false);

  const handleFiles = async (files) => {
    const clearInputs = () => {
      try {
        if (cameraRef.current) cameraRef.current.value = "";
        if (galleryRef.current) galleryRef.current.value = "";
      } catch (_) { /* ignore */ }
    };
    if (!files || !files.length) { clearInputs(); return; }
    if (!vr) {
      flash?.("Vacate request not loaded — refresh and try again.", "error");
      clearInputs();
      return;
    }
    setBusy(true);
    try {
      const url = await uploadPhoto(files[0], `vacate_${vr.id}`, { maxWidth: 1024, quality: 0.75 });
      const res = await api.vacateAddPhoto(vr.id, { category: 'damage_proof', url });
      if (res?.success) {
        setPhotos(prev => [...prev, res.photo]);
        onChange([...(selectedIds || []), res.photo.id]);
      } else {
        flash(res?.error || "Upload failed", "error");
      }
    } catch (e) {
      flash(e?.message || "Upload failed", "error");
    } finally {
      setBusy(false);
      clearInputs();
    }
  };

  const toggle = (pid) => {
    const cur = selectedIds || [];
    onChange(cur.includes(pid) ? cur.filter(x => x !== pid) : [...cur, pid]);
  };

  return (
    <div style={{ marginTop: 8 }}>
      <div style={{ fontSize: 10, color: h.ts, marginBottom: 4, fontWeight: 600 }}>Proof photos (optional)</div>
      {damageProofs.length > 0 && (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 4, marginBottom: 6 }}>
          {damageProofs.map(p => {
            const checked = (selectedIds || []).includes(p.id);
            return (
              <div key={p.id} onClick={() => toggle(p.id)} style={{
                position: "relative", aspectRatio: "1 / 1",
                borderRadius: rad.sm, overflow: "hidden",
                border: `2px solid ${checked ? h.ac : h.bd}`,
                cursor: "pointer",
              }}>
                <img src={p.url} alt="proof" loading="lazy" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                {checked && <div style={{ position: "absolute", top: 2, right: 2, width: 16, height: 16, borderRadius: 8, background: h.ac, color: "#fff", fontSize: 10, fontWeight: 700, display: "flex", alignItems: "center", justifyContent: "center" }}>✓</div>}
              </div>
            );
          })}
        </div>
      )}
      <div style={{ display: "flex", gap: 4 }}>
        <button onClick={() => cameraRef.current?.click()} disabled={busy} style={{
          flex: 1, padding: "6px 8px", borderRadius: rad.sm,
          border: `1px solid ${h.ac}`, background: `${h.ac}10`, color: h.ac,
          fontSize: 10, fontWeight: 600, cursor: busy ? "default" : "pointer",
        }}>{busy ? "..." : "📷 Take Proof"}</button>
        <button onClick={() => galleryRef.current?.click()} disabled={busy} style={{
          flex: 1, padding: "6px 8px", borderRadius: rad.sm,
          border: `1px dashed ${h.bd}`, background: "transparent", color: h.ts,
          fontSize: 10, fontWeight: 600, cursor: busy ? "default" : "pointer",
        }}>🖼 Gallery</button>
        {/* Tightened accept list — keeps iOS from offering HEIC files
            that the browser can't decode. See utils/supabaseClient.js
            for the upstream HEIC guard and 15s safety timeout. */}
        <input ref={cameraRef} type="file" accept="image/jpeg,image/png,image/webp" capture="environment" hidden onChange={e => handleFiles(e.target.files)} />
        <input ref={galleryRef} type="file" accept="image/jpeg,image/png,image/webp" hidden onChange={e => handleFiles(e.target.files)} />
      </div>
    </div>
  );
}

// Side-by-side photo comparison row for one category. Same reason
// for module-scope as ProofUploader — keeps the lightbox click
// handlers attached to stable DOM nodes across parent re-renders.
function PhotoCategoryRow({ category, moveOutByCat, moveInByCat, setLightboxUrl, h }) {
  const inOut = moveOutByCat[category] || [];
  // Move-in photos are now pre-grouped by category via captionToCategory()
  // (see parent's useMemo). Previously we filtered on every render with a
  // strict `.includes(category)` against the raw caption, which silently
  // failed for things like "Bed Room" (has a space) vs "bedroom".
  const inMatches = moveInByCat[category] || [];
  const meta = CATEGORY_META[category] || { label: category, icon: "📷" };
  return (
    <div style={{ ...crd(h), padding: 10, marginBottom: 8 }}>
      <div style={{ ...type.sm, color: h.tx, fontWeight: 700, marginBottom: 6 }}>
        <span style={{ marginRight: 6 }}>{meta.icon}</span>{meta.label}
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
        <div>
          <div style={{ fontSize: 9, color: h.ts, fontWeight: 600, marginBottom: 3 }}>MOVE-IN ({inMatches.length})</div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: 3 }}>
            {inMatches.length === 0
              ? <div style={{ gridColumn: '1 / -1', color: h.tt, fontSize: 10, padding: 6 }}>None matched</div>
              : inMatches.slice(0, 4).map((p, i) => (
                <img key={i} src={p.url} alt="" onClick={() => setLightboxUrl(p.url)} loading="lazy"
                  style={{ width: "100%", aspectRatio: "1 / 1", objectFit: "cover", borderRadius: 4, cursor: "pointer", border: `1px solid ${h.bd}` }} />
              ))}
          </div>
        </div>
        <div>
          <div style={{ fontSize: 9, color: h.ts, fontWeight: 600, marginBottom: 3 }}>MOVE-OUT ({inOut.length})</div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: 3 }}>
            {inOut.length === 0
              ? <div style={{ gridColumn: '1 / -1', color: h.tt, fontSize: 10, padding: 6 }}>Not uploaded</div>
              : inOut.slice(0, 4).map(p => (
                <img key={p.id} src={p.url} alt="" onClick={() => setLightboxUrl(p.url)} loading="lazy"
                  style={{ width: "100%", aspectRatio: "1 / 1", objectFit: "cover", borderRadius: 4, cursor: "pointer", border: `1px solid ${h.bd}` }} />
              ))}
          </div>
        </div>
      </div>
    </div>
  );
}

// Compact INR formatter that takes paise.
const fmtPaise = (paise) => "₹" + Math.round(Number(paise || 0) / 100).toLocaleString('en-IN');

export default function VacateDetailPage() {
  const { h, t, go, selVacateId, tens, props, leases, flash, showConfirm, refreshData } = useContext(Ctx);
  const [vr, setVr] = useState(null);
  const [photos, setPhotos] = useState([]);
  const [deductions, setDeductions] = useState([]);
  const [settlement, setSettlement] = useState(null); // populated when status=settled
  const [loading, setLoading] = useState(true);
  const [acting, setActing] = useState(false);
  const [landlordNotesDraft, setLandlordNotesDraft] = useState("");
  const [lightboxUrl, setLightboxUrl] = useState(null);
  const [showSettleSig, setShowSettleSig] = useState(false); // signature pad before settle

  // Composer draft (new deduction not yet saved)
  const emptyDraft = { reason: 'damage', amount_rs: '', description: '', proof_photo_ids: [] };
  const [draft, setDraft] = useState(emptyDraft);

  // Inline-edit state for an existing deduction. Declared up here
  // (alongside the other useStates) so the hook call order stays
  // stable across renders — otherwise the early returns below for
  // 'Loading…' / 'Not found' would conditionally skip these hooks
  // and React would flag it as a hooks-order violation.
  // Keeps a single editing id at a time so the row UI stays simple —
  // each row either renders read-only with Edit/Remove buttons, or
  // replaces its contents with a compact inline form.
  const [editingDedId, setEditingDedId] = useState(null);
  const [editDraft, setEditDraft] = useState({ amount_rs: "", description: "" });

  // Normalises a server-returned deduction row before putting it in
  // state. Server stores proof_photo_ids as a JSON-encoded TEXT
  // column ('["p1","p2"]'); the UI expects a real array so `.map()`
  // and `.length` work. Used by refresh(), addDeduction(), and
  // saveEditDeduction() so all three paths produce identical shapes.
  const normalizeDeduction = (d) => ({
    ...d,
    proof_photo_ids: (() => {
      const raw = d?.proof_photo_ids;
      if (Array.isArray(raw)) return raw;
      if (!raw) return [];
      try { return JSON.parse(raw); } catch { return []; }
    })(),
  });

  const refresh = async () => {
    if (!selVacateId) return;
    const res = await api.vacateGet(selVacateId);
    if (res?.success) {
      setVr(res.vacate_request);
      setPhotos(res.photos || []);
      // Server stores proof_photo_ids as JSON string; parse for UI use.
      setDeductions((res.deductions || []).map(normalizeDeduction));
      setLandlordNotesDraft(res.vacate_request.landlord_notes || "");
      setSettlement(res.settlement || null);
    }
  };

  useEffect(() => {
    (async () => {
      setLoading(true);
      try { await refresh(); }
      finally { setLoading(false); }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selVacateId]);

  if (loading) return <div style={{ paddingTop: 40, textAlign: 'center', color: h.ts }}>Loading…</div>;
  if (!vr) {
    // Two distinct dead-end states, both deserve an explanation +
    // a way out (a 'sorry, not found' message with no Back button
    // is a UX trap):
    //   - selVacateId is null  → the user landed here without
    //     picking a request first (state was dropped on refresh,
    //     stale link, etc.)
    //   - selVacateId is set but the row didn't come back → the
    //     request was cancelled, deleted, or hidden by RLS.
    const missingSelection = !selVacateId;
    return (
      <div style={{ paddingTop: 24, paddingBottom: 24, textAlign: 'center' }}>
        <div style={{
          margin: '0 auto', maxWidth: 360,
          background: h.cd, borderRadius: 16,
          padding: 24,
          border: `1px solid ${h.bd}`,
          boxShadow: h.sh,
        }}>
          <div style={{ fontSize: 36, marginBottom: 8 }}>🔍</div>
          <h3 style={{ ...type.h3, color: h.tx, margin: '0 0 8px' }}>
            {missingSelection ? "No vacate request selected" : "Couldn't load this vacate request"}
          </h3>
          <p style={{ color: h.sc, fontSize: 12, lineHeight: 1.5, margin: '0 0 16px' }}>
            {missingSelection
              ? "Pick a request from the inbox to see its details."
              : "It may have been cancelled, deleted, or moved by the tenant. Try the inbox to find the current list."}
          </p>
          <button onClick={() => go('vacateInbox')}
            style={{
              padding: "10px 18px", borderRadius: rad.md, border: 'none',
              background: h.ac, color: '#fff',
              fontSize: 13, fontWeight: 700, cursor: 'pointer',
              boxShadow: `0 4px 12px ${h.ac}30`,
            }}>
            ← Back to Vacate Inbox
          </button>
        </div>
      </div>
    );
  }

  const tenant = tens.find(x => x.id === vr.tenant_id);
  const prop = props.find(p => p.id === vr.property_id);
  const lease = leases.find(l => l.id === vr.lease_id);
  const depositRs = Number(lease?.deposit) || 0;
  const totalDeductionsPaise = deductions.reduce((s, d) => s + Number(d.amount || 0), 0);
  const refundPaise = Math.max(0, depositRs * 100 - totalDeductionsPaise);

  // Original move-in photos (from lease.terms.condition_photos),
  // pre-grouped by resolved category. See utils/vacatePhotos.js for
  // the synonym table that maps free-text captions like "Bed Room"
  // / "Home Theater" / "Toilet" to the structured categories the
  // vacate flow uses.
  const moveInPhotos = leaseMoveInPhotos(lease);
  const moveInByCat = groupMoveInByCategory(moveInPhotos);

  // Move-out (tenant uploaded) photos grouped by category
  const moveOutByCat = {};
  for (const p of photos) {
    if (p.uploaded_by_role !== 'tenant') continue;
    (moveOutByCat[p.category] = moveOutByCat[p.category] || []).push(p);
  }
  const damageProofs = photos.filter(p => p.uploaded_by_role === 'landlord' && p.category === 'damage_proof');

  // ── Actions ────────────────────────────────────────────────────
  const saveLandlordNotes = async () => {
    const res = await api.vacateUpdateNotes(vr.id, { landlord_notes: landlordNotesDraft });
    if (res?.success) { setVr(res.vacate_request); flash("Notes saved"); }
    else flash(res?.error || "Could not save notes", "error");
  };

  const approveFull = async () => {
    showConfirm("Approve full refund of the deposit (no deductions)?", async () => {
      setActing(true);
      try {
        const res = await api.vacateTransition(vr.id, 'approve');
        if (res?.success) { flash("Full refund proposed — awaiting tenant acceptance"); await refresh(); }
        else flash(res?.message || res?.error || "Could not approve", "error");
      } finally { setActing(false); }
    });
  };

  const proposeDeductions = async () => {
    if (deductions.length === 0) {
      flash("Add at least one deduction before proposing.", "error");
      return;
    }
    showConfirm(`Propose ${deductions.length} deduction${deductions.length > 1 ? 's' : ''} totalling ${fmtPaise(totalDeductionsPaise)}?`, async () => {
      setActing(true);
      try {
        const res = await api.vacateTransition(vr.id, 'propose');
        if (res?.success) { flash("Deductions proposed — awaiting tenant"); await refresh(); }
        else flash(res?.message || res?.error || "Could not propose", "error");
      } finally { setActing(false); }
    });
  };

  // Phase 4: settle — capture landlord signature then trigger the
  // server's terminal transition. Server side-effect detaches the
  // tenant (vac=0, pid=NULL), frees the property + units (occ=0,
  // tid=NULL), and terminates the lease.
  //
  // CRITICAL: after settle we must call BOTH:
  //   - refresh()      → re-fetches this vacate request (so the
  //                      detail view shows the settled state with
  //                      the new deposit_settlements row).
  //   - refreshData()  → re-syncs the global tens/props/leases
  //                      arrays so the LandlordDash + PropsListPage
  //                      stop showing 'Occupied' / the tenant card
  //                      for what's now a vacant property.
  // Without refreshData() the database is correct but every other
  // screen in the app stays stale until the next pull-to-refresh.
  const finaliseSettlement = async (signatureDataUrl) => {
    if (!vr) return;
    setActing(true);
    try {
      const res = await api.vacateTransition(vr.id, 'settle', { signature: signatureDataUrl });
      if (res?.success) {
        setShowSettleSig(false);
        flash("Settlement finalised — deposit refund processed, property freed");
        await refresh();
        // Global re-sync — fires the same code path as a pull-to-
        // refresh. Side-effects of settle (tenant detach, property
        // free, lease terminate) all become visible across the app.
        try { await refreshData?.(); } catch (_) { /* non-fatal */ }
      } else {
        flash(res?.message || res?.error || "Could not settle", "error");
      }
    } finally {
      setActing(false);
    }
  };

  const addDeduction = async () => {
    const rupees = Number(draft.amount_rs);
    if (!Number.isFinite(rupees) || rupees <= 0) { flash("Enter a positive amount", "error"); return; }
    const amount_paise = Math.round(rupees * 100);
    const res = await api.vacateAddDeduction(vr.id, {
      reason: draft.reason,
      amount_paise,
      description: draft.description.trim(),
      proof_photo_ids: draft.proof_photo_ids,
    });
    if (res?.success) {
      setDeductions(prev => [...prev, normalizeDeduction(res.deduction)]);
      if (res.warning) flash(res.warning.message, "error");
      else flash("Deduction added");
      setDraft(emptyDraft);
    } else {
      flash(res?.error || "Could not add deduction", "error");
    }
  };

  const removeDeduction = async (did) => {
    const res = await api.vacateDeleteDeduction(vr.id, did);
    if (res?.success) {
      setDeductions(prev => prev.filter(d => d.id !== did));
      flash("Removed");
    } else flash(res?.error || "Could not remove", "error");
  };

  // Note: editingDedId / editDraft state is declared above with the
  // other useStates so the hook call order stays stable across the
  // early-return paths for loading / not-found.
  const beginEditDeduction = (d) => {
    setEditingDedId(d.id);
    setEditDraft({
      amount_rs: Math.round((d.amount || 0) / 100).toString(),
      description: d.description || "",
    });
  };
  const cancelEditDeduction = () => {
    setEditingDedId(null);
    setEditDraft({ amount_rs: "", description: "" });
  };
  const saveEditDeduction = async (did) => {
    const rupees = Number(editDraft.amount_rs);
    if (!isFinite(rupees) || rupees < 0) {
      flash("Enter a valid amount (≥ ₹0)", "error");
      return;
    }
    const res = await api.vacateUpdateDeduction(vr.id, did, {
      amount_paise: Math.round(rupees * 100),
      description: editDraft.description,
    });
    if (res?.success && res.deduction) {
      // normalizeDeduction parses proof_photo_ids from the JSON
      // TEXT column the server stores into a real array. Without
      // this the row render calls .map() on a string and crashes.
      setDeductions(prev => prev.map(d => d.id === did ? normalizeDeduction(res.deduction) : d));
      setEditingDedId(null);
      flash("Updated");
    } else {
      flash(res?.error || "Could not update", "error");
    }
  };

  // canReview unlocks the deduction composer + propose buttons.
  // 'disputed' is included so the landlord can mutually resolve a
  // tenant dispute by striking unfair deductions and re-proposing
  // (or switching to full refund) — without escalating to admin.
  // Server state machine matches this — propose & approve are both
  // valid from disputed.
  const canReview = ['under_review','full_refund_proposed','deduction_proposed','disputed'].includes(vr.status);
  // canActOnButtons enables the Approve / Propose CTA pair. 'disputed'
  // joins the list so the landlord can mutually resolve by re-
  // proposing (strike unfair deductions) or switching to full refund
  // without waiting for admin.
  const canActOnButtons = vr.status === 'under_review' || vr.status === 'full_refund_proposed' || vr.status === 'disputed';
  const isRevising = vr.status === 'disputed';

  // Note: ProofUploader and PhotoCategoryRow are defined at module
  // scope above (search for them in this file). They take what they
  // need via props rather than closing over parent state.

  return (
    <div style={{ paddingTop: 16 }}>
      <button onClick={() => go("vacateInbox")} style={{ background: "none", border: "none", color: h.ac, cursor: "pointer", fontSize: 13, marginBottom: 8 }}>← Back to Inbox</button>

      {/* Header card */}
      <div style={{ ...crd(h), padding: 14, marginBottom: 12 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 8 }}>
          <div>
            <div style={{ ...type.h2, color: h.tx }}>{tenant?.name || vr.tenant_id}</div>
            <div style={{ color: h.sc, fontSize: 12, marginTop: 2 }}>{prop ? formatPropAddr(prop) : vr.property_id}</div>
            <div style={{ color: h.tt, fontSize: 10, marginTop: 2 }}>
              Deposit: <b style={{ color: h.tx }}>₹{depositRs.toLocaleString('en-IN')}</b>
            </div>
          </div>
          <span style={{
            fontSize: 10, fontWeight: 700, textTransform: "uppercase",
            padding: "4px 10px", borderRadius: 999,
            background: `${h.ac}15`, color: h.ac,
          }}>{vr.status.replace(/_/g, ' ')}</span>
        </div>
      </div>

      {/* Tenant notes */}
      {vr.tenant_notes && (
        <div style={{ ...crd(h), padding: 12, marginBottom: 12, background: `${h.ac}05`, borderLeft: `3px solid ${h.ac}` }}>
          <div style={{ ...type.cap, color: h.ts, marginBottom: 4 }}>📝 Tenant's notes</div>
          <div style={{ color: h.sc, fontSize: 12, lineHeight: 1.5, whiteSpace: "pre-wrap" }}>{vr.tenant_notes}</div>
        </div>
      )}

      {/* Side-by-side photo comparison */}
      <div style={{ marginBottom: 12 }}>
        <div style={{ ...type.cap, color: h.ts, marginBottom: 6 }}>Photo comparison · move-in vs move-out</div>
        {['hall','bedroom','kitchen','bathroom','walls','meter'].map(cat =>
          <PhotoCategoryRow key={cat} category={cat}
            moveOutByCat={moveOutByCat} moveInByCat={moveInByCat}
            setLightboxUrl={setLightboxUrl} h={h} />
        )}
        {/* Extra "Other" rows for photos that don't fit the six fixed
            categories. We render two separate strips — one for lease
            condition photos with unmappable captions (e.g. "Home
            Theater", "Backyard"), one for tenant move-out uploads
            tagged 'other' — so the landlord can still review them
            during settlement without losing them in the comparison. */}
        {(moveInByCat['other'] || []).length > 0 && (
          <div style={{ ...crd(h), padding: 10, marginBottom: 8 }}>
            <div style={{ ...type.sm, color: h.tx, fontWeight: 700, marginBottom: 6 }}>📷 Other (move-in)</div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 4 }}>
              {moveInByCat['other'].map((p, i) => (
                <div key={i} style={{ position: "relative" }}>
                  <img src={p.url} alt={p.caption || ""} onClick={() => setLightboxUrl(p.url)} loading="lazy"
                    style={{ width: "100%", aspectRatio: "1 / 1", objectFit: "cover", borderRadius: 4, cursor: "pointer", border: `1px solid ${h.bd}` }} />
                  {p.caption && (
                    <div style={{
                      position: "absolute", bottom: 0, left: 0, right: 0,
                      background: "linear-gradient(transparent, rgba(0,0,0,0.7))",
                      color: "#fff", fontSize: 8, fontWeight: 600,
                      padding: "8px 4px 2px",
                      textOverflow: "ellipsis", overflow: "hidden", whiteSpace: "nowrap",
                    }}>{p.caption}</div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
        {(moveOutByCat['other'] || []).length > 0 && (
          <div style={{ ...crd(h), padding: 10, marginBottom: 8 }}>
            <div style={{ ...type.sm, color: h.tx, fontWeight: 700, marginBottom: 6 }}>📷 Other (tenant move-out)</div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 4 }}>
              {moveOutByCat['other'].map(p => (
                <img key={p.id} src={p.url} alt="" onClick={() => setLightboxUrl(p.url)} loading="lazy"
                  style={{ width: "100%", aspectRatio: "1 / 1", objectFit: "cover", borderRadius: 4, cursor: "pointer", border: `1px solid ${h.bd}` }} />
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Landlord notes */}
      {canReview && (
        <div style={{ ...crd(h), padding: 12, marginBottom: 12 }}>
          <div style={{ ...type.cap, color: h.ts, marginBottom: 6 }}>Your notes (visible to tenant)</div>
          <textarea
            value={landlordNotesDraft}
            onChange={e => setLandlordNotesDraft(e.target.value)}
            placeholder="Add any context for the tenant…"
            rows={3}
            maxLength={1000}
            style={{
              width: "100%", padding: 8, borderRadius: rad.sm,
              border: `1px solid ${h.bd}`, background: h.bg, color: h.tx,
              fontSize: 12, fontFamily: "inherit", resize: "vertical",
              boxSizing: "border-box",
            }}
          />
          <button onClick={saveLandlordNotes} style={{ ...btnSec(h), marginTop: 6, padding: "6px 12px", fontSize: 11 }}>Save Notes</button>
        </div>
      )}

      {/* Deduction composer */}
      {canReview && (
        <div style={{ ...crd(h), padding: 12, marginBottom: 12 }}>
          <div style={{ ...type.cap, color: h.ts, marginBottom: 8 }}>💸 Deductions ({deductions.length})</div>

          {/* Existing line items. Edit mode lets the landlord REDUCE
              an amount or rewrite the description inline — better
              than 'delete and re-add' during dispute negotiation
              (e.g. drop ₹10k painting to ₹5k as a compromise). */}
          {deductions.length > 0 && deductions.map(d => {
            const isEditing = editingDedId === d.id;
            return (
              <div key={d.id} style={{
                padding: 10, borderRadius: rad.sm,
                border: `1px solid ${isEditing ? h.ac : h.bd}`,
                marginBottom: 6,
                background: isEditing ? `${h.ac}08` : `${h.ac}03`,
              }}>
                {isEditing ? (
                  // ── Inline edit form ────────────────────────────
                  <div>
                    <div style={{ ...type.sm, color: h.tx, fontWeight: 700, marginBottom: 6 }}>
                      {DEDUCTION_REASONS.find(r => r.id === d.reason)?.label || d.reason}
                    </div>
                    <label style={{ ...type.cap, color: h.ts, display: "block", marginBottom: 2 }}>Amount (₹)</label>
                    <input
                      type="number"
                      min={0}
                      value={editDraft.amount_rs}
                      onChange={e => setEditDraft(s => ({ ...s, amount_rs: e.target.value }))}
                      style={{
                        width: "100%", padding: "6px 8px", borderRadius: rad.sm,
                        border: `1px solid ${h.bd}`, background: h.bg, color: h.tx,
                        fontSize: 12, marginBottom: 6, boxSizing: "border-box",
                      }}
                    />
                    <label style={{ ...type.cap, color: h.ts, display: "block", marginBottom: 2 }}>Description</label>
                    <textarea
                      value={editDraft.description}
                      onChange={e => setEditDraft(s => ({ ...s, description: e.target.value }))}
                      rows={2}
                      maxLength={500}
                      style={{
                        width: "100%", padding: "6px 8px", borderRadius: rad.sm,
                        border: `1px solid ${h.bd}`, background: h.bg, color: h.tx,
                        fontSize: 12, fontFamily: "inherit", resize: "vertical",
                        marginBottom: 6, boxSizing: "border-box",
                      }}
                    />
                    <div style={{ display: "flex", gap: 6 }}>
                      <button onClick={cancelEditDeduction}
                        style={{
                          flex: 1, padding: 8, borderRadius: rad.sm,
                          border: `1px solid ${h.bd}`, background: "transparent",
                          color: h.ts, fontSize: 11, fontWeight: 600, cursor: "pointer",
                        }}>Cancel</button>
                      <button onClick={() => saveEditDeduction(d.id)}
                        style={{
                          flex: 2, padding: 8, borderRadius: rad.sm, border: "none",
                          background: h.ac, color: "#fff",
                          fontSize: 11, fontWeight: 700, cursor: "pointer",
                        }}>Save</button>
                    </div>
                  </div>
                ) : (
                  // ── Read-only row ──────────────────────────────
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ ...type.sm, color: h.tx, fontWeight: 700 }}>
                        {DEDUCTION_REASONS.find(r => r.id === d.reason)?.label || d.reason}
                      </div>
                      {d.description && <div style={{ color: h.sc, fontSize: 11, marginTop: 2 }}>{d.description}</div>}
                      {(d.proof_photo_ids || []).length > 0 && (
                        <div style={{ display: "flex", gap: 3, marginTop: 4 }}>
                          {d.proof_photo_ids.map(pid => {
                            const p = damageProofs.find(x => x.id === pid);
                            if (!p) return null;
                            return <img key={pid} src={p.url} alt="" onClick={() => setLightboxUrl(p.url)} style={{ width: 36, height: 36, objectFit: "cover", borderRadius: 4, cursor: "pointer", border: `1px solid ${h.bd}` }} />;
                          })}
                        </div>
                      )}
                    </div>
                    <div style={{ textAlign: "right", flexShrink: 0, marginLeft: 8 }}>
                      <div style={{ ...type.sm, color: h.rd, fontWeight: 700 }}>-{fmtPaise(d.amount)}</div>
                      {canReview && (
                        <div style={{ display: "flex", gap: 4, marginTop: 4, justifyContent: "flex-end" }}>
                          <button onClick={() => beginEditDeduction(d)}
                            style={{ background: "none", border: "none", color: h.ac, cursor: "pointer", fontSize: 10, fontWeight: 600 }}>
                            Edit
                          </button>
                          <button onClick={() => removeDeduction(d.id)}
                            style={{ background: "none", border: "none", color: h.tt, cursor: "pointer", fontSize: 10 }}>
                            Remove
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            );
          })}

          {/* New line item form */}
          <div style={{
            padding: 10, borderRadius: rad.sm,
            border: `2px dashed ${h.bd}`,
            marginTop: deductions.length > 0 ? 4 : 0,
          }}>
            <div style={{ ...type.cap, color: h.ts, marginBottom: 6 }}>Add a deduction</div>
            <select
              value={draft.reason}
              onChange={e => setDraft({ ...draft, reason: e.target.value })}
              style={{ width: "100%", padding: 8, borderRadius: rad.sm, border: `1px solid ${h.bd}`, background: h.bg, color: h.tx, fontSize: 12, marginBottom: 6, boxSizing: "border-box" }}
            >
              {DEDUCTION_REASONS.map(r => <option key={r.id} value={r.id}>{r.label}</option>)}
            </select>
            <input
              type="number"
              value={draft.amount_rs}
              onChange={e => setDraft({ ...draft, amount_rs: e.target.value })}
              placeholder="Amount in ₹ (e.g. 1500)"
              min="1"
              style={{ width: "100%", padding: 8, borderRadius: rad.sm, border: `1px solid ${h.bd}`, background: h.bg, color: h.tx, fontSize: 12, marginBottom: 6, boxSizing: "border-box" }}
            />
            <textarea
              value={draft.description}
              onChange={e => setDraft({ ...draft, description: e.target.value })}
              placeholder="Describe the deduction (e.g. 'Broken AC remote, cracked tile near sink')"
              rows={2}
              maxLength={500}
              style={{ width: "100%", padding: 8, borderRadius: rad.sm, border: `1px solid ${h.bd}`, background: h.bg, color: h.tx, fontSize: 12, fontFamily: "inherit", resize: "vertical", marginBottom: 6, boxSizing: "border-box" }}
            />
            <ProofUploader
              selectedIds={draft.proof_photo_ids}
              onChange={(ids) => setDraft({ ...draft, proof_photo_ids: ids })}
              vr={vr}
              damageProofs={damageProofs}
              setPhotos={setPhotos}
              flash={flash}
              h={h}
            />
            <button onClick={addDeduction} style={{ ...btnP(h), width: "100%", padding: 10, fontSize: 12, marginTop: 8 }}>
              + Add Deduction
            </button>
          </div>

          {/* Refund summary */}
          <div style={{
            marginTop: 12, padding: 10,
            background: h.ib, borderRadius: rad.sm,
          }}>
            <div style={{ display: "flex", justifyContent: "space-between", fontSize: 11, color: h.sc, marginBottom: 4 }}>
              <span>Deposit</span>
              <span>₹{depositRs.toLocaleString('en-IN')}</span>
            </div>
            <div style={{ display: "flex", justifyContent: "space-between", fontSize: 11, color: h.rd, marginBottom: 4 }}>
              <span>Total deductions</span>
              <span>-{fmtPaise(totalDeductionsPaise)}</span>
            </div>
            <div style={{ height: 1, background: h.bd, margin: "6px 0" }} />
            <div style={{ display: "flex", justifyContent: "space-between", fontSize: 13, fontWeight: 700 }}>
              <span style={{ color: h.tx }}>Refund to tenant</span>
              <span style={{ color: refundPaise > 0 ? h.gn : h.ts }}>{fmtPaise(refundPaise)}</span>
            </div>
            {totalDeductionsPaise > depositRs * 100 && (
              <div style={{ marginTop: 6, padding: 6, background: `${h.wn}15`, borderRadius: 4, color: h.wn, fontSize: 10 }}>
                ⚠️ Total deductions exceed deposit. Refund will be clamped to ₹0 at settle.
              </div>
            )}
          </div>
        </div>
      )}

      {/* Primary action buttons. When revising in response to a
          dispute, labels shift to make the intent clear — the
          landlord is responding to the tenant's objection, not
          proposing from scratch. */}
      {canActOnButtons && (
        <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
          <button onClick={approveFull} disabled={acting} style={{
            flex: 1, padding: 12, borderRadius: rad.md,
            border: `1px solid ${h.gn}`, background: `${h.gn}10`, color: h.gn,
            fontWeight: 700, fontSize: 12, cursor: acting ? "default" : "pointer",
          }}>{isRevising ? "✅ Switch to Full Refund" : "✅ Approve Full Refund"}</button>
          <button onClick={proposeDeductions} disabled={acting || deductions.length === 0} style={{
            flex: 1, padding: 12, borderRadius: rad.md, border: "none",
            background: deductions.length === 0 ? h.bd : h.ac, color: "#fff",
            fontWeight: 700, fontSize: 12,
            cursor: (acting || deductions.length === 0) ? "not-allowed" : "pointer",
            opacity: (acting || deductions.length === 0) ? 0.55 : 1,
          }}>{isRevising ? "✏️ Re-propose to Tenant" : "💸 Propose Deductions"}</button>
        </div>
      )}

      {/* Status footers for non-actionable states */}
      {vr.status === 'deduction_proposed' && (
        <div style={{ ...crd(h), padding: 12, marginBottom: 12, background: `${h.ac}05`, borderLeft: `3px solid ${h.ac}` }}>
          <div style={{ color: h.ac, fontWeight: 700, fontSize: 12, marginBottom: 4 }}>Awaiting tenant response</div>
          <div style={{ color: h.sc, fontSize: 11, lineHeight: 1.5 }}>
            Tenant has been notified. They can accept or dispute. You can still add or remove deductions
            until they respond.
          </div>
        </div>
      )}
      {vr.status === 'full_refund_proposed' && (
        <div style={{ ...crd(h), padding: 12, marginBottom: 12, background: `${h.gn}05`, borderLeft: `3px solid ${h.gn}` }}>
          <div style={{ color: h.gn, fontWeight: 700, fontSize: 12, marginBottom: 4 }}>Full refund proposed</div>
          <div style={{ color: h.sc, fontSize: 11, lineHeight: 1.5 }}>
            Awaiting tenant acceptance to settle. You can still propose deductions instead if circumstances change.
          </div>
        </div>
      )}

      {/* Disputed — two paths forward:
            1. Mutually resolve: strike unfair deductions / switch
               to full refund, then re-propose. Tenant gets notified.
            2. Stand by your proposal: wait for admin ruling. */}
      {vr.status === 'disputed' && (
        <div style={{ ...crd(h), padding: 12, marginBottom: 12, background: `${h.wn}10`, border: `1px solid ${h.wn}40` }}>
          <div style={{ color: h.wn, fontWeight: 700, fontSize: 12, marginBottom: 4 }}>🚩 Tenant disputed — your move</div>
          {vr.dispute_reason && (
            <div style={{ color: h.sc, fontSize: 11, lineHeight: 1.5, marginBottom: 6, whiteSpace: 'pre-wrap' }}>
              <b>Tenant's reason:</b> {vr.dispute_reason}
            </div>
          )}
          <div style={{ color: h.tx, fontSize: 11, lineHeight: 1.5, marginBottom: 4, fontWeight: 600 }}>
            You can resolve this two ways:
          </div>
          <ul style={{ color: h.sc, fontSize: 11, lineHeight: 1.55, margin: '0 0 4px', paddingLeft: 18 }}>
            <li>
              <b style={{ color: h.tx }}>Revise your proposal</b> — strike out the deductions you think
              are unfair (or switch to a full refund) and re-propose. The tenant will be notified and can
              accept the revised amount directly.
            </li>
            <li>
              <b style={{ color: h.tx }}>Stand by your proposal</b> — wait for a super admin to review the
              dispute and post a binding ruling. You'll be able to settle once that lands.
            </li>
          </ul>
        </div>
      )}

      {/* Agreed — ready to settle. Capture landlord signature, then
          trigger the terminal transition that writes the deposit ledger,
          detaches the tenant, and closes the lease. */}
      {vr.status === 'agreed' && (
        <div style={{ ...crd(h), padding: 14, marginBottom: 12, background: `${h.ac}05`, border: `2px solid ${h.ac}30` }}>
          <div style={{ ...type.cap, color: h.ac, marginBottom: 8, fontWeight: 700 }}>
            {vr.admin_resolution_note ? '⚖️ Admin ruling posted — finalise the settlement' : '🤝 Tenant accepted — finalise the settlement'}
          </div>
          {vr.admin_resolution_note && (
            <div style={{ background: h.bg, padding: 8, borderRadius: rad.sm, marginBottom: 10, border: `1px solid ${h.bd}` }}>
              <div style={{ fontSize: 9, color: h.ts, fontWeight: 700, marginBottom: 3, letterSpacing: 0.3 }}>ADMIN'S RULING</div>
              <div style={{ color: h.tx, fontSize: 12, lineHeight: 1.5, whiteSpace: 'pre-wrap' }}>{vr.admin_resolution_note}</div>
            </div>
          )}
          {vr.tenant_signature && (
            <div style={{ background: h.bg, padding: 8, borderRadius: rad.sm, marginBottom: 10, border: `1px solid ${h.bd}` }}>
              <div style={{ fontSize: 9, color: h.ts, fontWeight: 700, marginBottom: 4, letterSpacing: 0.3 }}>TENANT'S SIGNATURE</div>
              <img src={vr.tenant_signature} alt="tenant sig" style={{ maxHeight: 50, background: "#fff", padding: 4, borderRadius: 6, display: "block" }} />
            </div>
          )}
          <div style={{ color: h.sc, fontSize: 11, lineHeight: 1.5, marginBottom: 10 }}>
            Refund amount: <b>{fmtPaise(refundPaise)}</b>. Sign below to write the deposit settlement
            record, terminate the lease, and detach the tenant from your organisation.
          </div>
          {showSettleSig ? (
            <SignaturePad h={h}
              onSave={finaliseSettlement}
              onCancel={() => setShowSettleSig(false)}
            />
          ) : (
            <button onClick={() => setShowSettleSig(true)} disabled={acting} style={{
              width: "100%", padding: 12, borderRadius: rad.md,
              border: "none", background: h.gn, color: "#fff",
              fontWeight: 700, fontSize: 13,
              cursor: acting ? "not-allowed" : "pointer",
            }}>{acting ? "Settling..." : "✍️ Sign & Finalise Settlement"}</button>
          )}
        </div>
      )}

      {/* Settled — final receipt with both signatures */}
      {vr.status === 'settled' && settlement && (
        <div style={{ ...crd(h), padding: 14, marginBottom: 12, background: `${h.gn}08`, border: `2px solid ${h.gn}30` }}>
          <div style={{ ...type.cap, color: h.gn, marginBottom: 8, fontWeight: 700 }}>
            ✓ SETTLED — agreement closed
          </div>
          <div style={{ background: h.bg, padding: 10, borderRadius: rad.sm, marginBottom: 10 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: h.sc, marginBottom: 4 }}>
              <span>Deposit</span><span>{fmtPaise(settlement.deposit_amount)}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: h.rd, marginBottom: 4 }}>
              <span>Total deductions</span><span>-{fmtPaise(settlement.total_deductions)}</span>
            </div>
            <div style={{ height: 1, background: h.bd, margin: '6px 0' }} />
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 15, fontWeight: 700 }}>
              <span style={{ color: h.tx }}>Refund paid</span>
              <span style={{ color: h.gn }}>{fmtPaise(settlement.refund_amount)}</span>
            </div>
          </div>
          {/* Both signatures */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 8 }}>
            <div style={{ background: h.bg, padding: 8, borderRadius: rad.sm, border: `1px solid ${h.bd}` }}>
              <div style={{ fontSize: 9, color: h.ts, fontWeight: 700, marginBottom: 4, letterSpacing: 0.3 }}>TENANT</div>
              {settlement.tenant_signature
                ? <img src={settlement.tenant_signature} alt="" style={{ maxHeight: 40, background: "#fff", padding: 4, borderRadius: 6, display: "block" }} />
                : <div style={{ fontSize: 10, color: h.tt }}>No signature</div>}
            </div>
            <div style={{ background: h.bg, padding: 8, borderRadius: rad.sm, border: `1px solid ${h.bd}` }}>
              <div style={{ fontSize: 9, color: h.ts, fontWeight: 700, marginBottom: 4, letterSpacing: 0.3 }}>LANDLORD</div>
              {settlement.landlord_signature
                ? <img src={settlement.landlord_signature} alt="" style={{ maxHeight: 40, background: "#fff", padding: 4, borderRadius: 6, display: "block" }} />
                : <div style={{ fontSize: 10, color: h.tt }}>No signature</div>}
            </div>
          </div>
          <div style={{ fontSize: 10, color: h.ts, lineHeight: 1.5, marginBottom: 8 }}>
            Settled on {new Date(settlement.settled_at).toLocaleDateString('en-IN')} · Resolution: {settlement.resolution.replace(/_/g,' ')}
          </div>
          <button onClick={() => openSettlementPdf({
            settlement, vacate_request: vr, deductions, photos,
            lease, tenant, property: prop,
          })} style={{
            width: "100%", padding: 10, borderRadius: rad.md, border: "none",
            background: h.ac, color: "#fff", fontWeight: 700, fontSize: 12,
            cursor: "pointer",
          }}>📄 Download Settlement Receipt</button>
        </div>
      )}

      {/* Lightbox */}
      {lightboxUrl && (
        <div onClick={() => setLightboxUrl(null)} style={{
          position: "fixed", inset: 0, background: "rgba(0,0,0,0.9)",
          zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center", padding: 16,
        }}>
          <img src={lightboxUrl} alt="" style={{ maxWidth: "100%", maxHeight: "90vh", objectFit: "contain", borderRadius: 8 }} />
        </div>
      )}
    </div>
  );
}
